// NOTE! this only works thanks to
//   '<file-name>': ['webpack']
// in karma.conf.js
import { Subject } from 'rxjs';
import { retry, take, takeUntil } from 'rxjs/operators';

import { Sentry } from '@shared/app/scripts/services/sentry';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';

/**
 * @ngdoc service
 * @name posApp.payPlaza
 * @description
 * Service for communication with v4.0 of the PayPlaza payment gateway. The payPlaza
 * ECR webservice v4.0 documentation is located at http://help.softwear.nl/display/SWUI/PayPlaza+Documentation
 */
angular
  .module('posApp')
  .factory(
    'payPlaza',
    (
      $log: ng.ILogService,
      $q: ng.IQService,
      sentry: Sentry,
      $rootScope: ng.IRootScopeService,
      displayMessage: any,
      mask: any,
      payPlazaRequests: any,
      payPlazaStreams: any,
      paymentTypes: any,
      pos: any,
      settingsManager: SettingsManager,
      translate: any,
    ) => {
      const $translate = translate;

      // Hack! To be replaced by custom operator rx operator
      const wrapInSafeApply = require('../../../../global/wrap-in-safe-apply')(
        $rootScope,
      );

      const payPlaza: any = {
          /**
           * @ngdoc
           * @name posApp.payPlaza#transactionId
           * @propertyOf posApp.payPlaza
           * @description
           * Value of transactionId for the currently transaction we are dealing with.
           * Provided by pay plaza. When changed {@link posApp.payPlaza#transactionId$}
           * emits
           */
          // TODO refactor to be private variable
          transactionId: undefined,
          /**
           * @ngdoc
           * @name posApp.payPlaza#merchRef
           * @propertyOf posApp.payPlaza
           * @description
           * Value of read from transaction, when it was started
           */
          // TODO refactor to be private variable
          merchRef: undefined,
          /**
           * @ngdoc
           * @name posApp.payPlaza#amount
           * @propertyOf posApp.payPlaza
           * @description
           * number, value of the current transaction, bigger than 0 for purchases,
           * smaller than 0 for refunds
           */
          // TODO refactor to be private variable
          amount: 0,
        },
        RETRYLIMIT = 5;

      let pendingTransactionDeferred;
      const printSubject = new Subject();
      const appNameSubject = new Subject();
      const actionSubject = new Subject();
      const updateSubject = new Subject();
      const inProgressSubject = new Subject();
      const errorMessageSubject = new Subject();
      const retryMessageSubject = new Subject();
      const transactionIdSubject = new Subject();
      const showTransactionMethodsSubject = new Subject();

      /**
       * @ngdoc
       * @name posApp.payPlaza#showTransactionMethods$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable; emits string each time there is a showTransactionMethods event
       * out form pay plaza
       */
      payPlaza.showTransactionMethods$ =
        showTransactionMethodsSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#retryMessage$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable; emits string each time there is a retryMessage event
       * out form pay plaza
       */
      payPlaza.retryMessage$ = retryMessageSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#errorMessage$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable; that emits message string each time there is a errorMessage event
       * out form pay plaza
       */
      payPlaza.errorMessage$ = errorMessageSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#transactionId$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable; that emits transactionId value every time when we receive
       * it form for pay plaza
       */
      payPlaza.transactionId$ = transactionIdSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#inProgress$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable; that emits true when the transaction is updated to be
       * in progress, and false when it's terminated
       */
      payPlaza.inProgress$ = inProgressSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#print$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable, emits printable content, every time when when we get
       * printing order form pay plaza
       */
      payPlaza.print$ = printSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#appName$
       * @propertyOf posApp.payPlaza
       * @description
       * rxjs's observable, emits card's application
       * (https://en.wikipedia.org/wiki/EMV#Application_selection) when pay plaza
       * provides this information - in print out
       */
      payPlaza.appName$ = appNameSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#action$
       * @propertyOf posApp.payPlaza
       * @description
       * stream that emits every time some method is called
       */
      payPlaza.action$ = actionSubject.asObservable();

      /**
       * @ngdoc
       * @name posApp.payPlaza#update$
       * @propertyOf posApp.payPlaza
       * @description
       * stream that emits string every time there is an updated from pay plaza
       */
      payPlaza.update$ = updateSubject.asObservable();

      /*
       * This method returns a formatted PIN receipt string.
       * The PIN receipt is sent in the response as an array of strings that
       * must be concatenated with new lines to create a complete PIN receipt
       * @param {object} formattedReceipt
       * object {lines: ['line 1', 'line 2']}
       * @returns {String} the formatted receipt string
       */
      function formatReceipt(formattedReceipt) {
        if (formattedReceipt && formattedReceipt.lines) {
          // Extra new lines to print the PIN data more seperately, incl. spaces because the globalevel will replace all the new lines in one new line
          // whitespace voo-doo
          return '\n   \n   \n' + formattedReceipt.lines.join('\n') + '\n ';
        } else {
          return '';
        }
      }

      /**
       * This method sends the PIN receipt to the print service.
       * returns the promise from print
       */
      function printHelper(printCommand) {
        printSubject.next(
          formatReceipt(printCommand.formattedReceipt) + '\n\n\n\n',
        );
      }

      function ppLogErrorHandler(message) {
        $log.error('[PayPlaza] ' + message);
        sentry.captureException(new Error('[PayPlaza] ' + message));
      }

      /**
       * Process a PayPlaza web service error by retrying failed transactions depending on the issue
       * Errors are retried five times before giving up and breaking the connection.
       */
      function ppError(resp) {
        let errorMsg;

        if (resp.cancelled) {
          // Ignore cancelled requests
          $log.warn('[PayPlaza] Cancelled request, ignoring!');
          return;
        }

        // If server responds with 500, 502, 504 then service is unavailble
        if (angular.isNumber(resp.status) && resp.status !== 200) {
          errorMessageSubject.next('PINServiceUnavailable');

          errorMsg = 'ECR web service error: ' + resp.status;
        } else {
          // TODO cover with test
          retryMessageSubject.next('PINErrorFatal');
          errorMsg = 'Encountered a error with the ECR web service: ' + status;
        }

        ppFatalErrorHandler(errorMsg);
      }

      /**
       * Processes a PayPlaza ECR API response body
       * handles the incoming message from the PIN terminal.
       * In most cases, it ends with a new request to keep the long-poll cycle running.
       * @param {object} d the response from the ecr webservice
       */
      function ppProc(d) {
        let msg = '',
          aid,
          applicationName,
          errorMsg;

        function sendPrintResultOnError() {
          // Send print result message to payPlaza
          payPlazaStreams.sendPrintSuccess$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));

          resetPayPlazaPIN();
        }

        function sendPrintResult() {
          // Send print result message to payPlaza
          payPlazaStreams.sendPrintSuccess$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
        }

        if (d.cancelled) {
          $log.warn('[PayPlaza] Cancelled request, ignoring!');
          return;
        }

        /*
         * If a timeout message is received, the protocol is to send another GET
         * to the ECR API until a different message is received
         */
        if (d.timeoutMessage) {
          msg = 'Timeout';
          $log.log(
            '[PayPlaza] TIMEOUT: ' + msg + ' Trans: ' + payPlaza.transactionId,
            d,
          );

          payPlazaStreams.poll$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
        }

        if (d.connectCompleteMessage) {
          if (d.connectCompleteMessage.connectResultCode === 'READY') {
            if (
              d.connectCompleteMessage.terminalWakeUpIpAddress &&
              d.connectCompleteMessage.terminalWakeUpIpPort
            ) {
              payPlazaRequests.wakeNFC(
                d.connectCompleteMessage.terminalWakeUpIpAddress,
                d.connectCompleteMessage.terminalWakeUpIpPort,
              );
            }

            sendTransactionResponseMessage();
          }

          if (d.connectCompleteMessage.connectResultCode === 'PRINT_RECEIPT') {
            payPlazaStreams.requestReprint$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
          }
        }

        /*
         * This message is received once the printing of the PIN receipt is complete
         * From ths point we either disconnect or continue Polling when instructed to
         * A continuation of polling usually means another printout is pending such
         * as a signature receipt for a credit card payment
         */
        if (d.printCompleteMessage) {
          $log.log(
            '[PayPlaza] PRINT COMPLETE: ' + d.printCompleteMessage.resultCode,
            d,
          );

          if (d.printCompleteMessage.nextAction === 'sendDisconnectMessage') {
            payPlazaStreams.disconnect$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
          } else if (
            d.printCompleteMessage.nextAction === 'waitForPrintCommandMessage'
          ) {
            // We are not finished printing, there can be a second merchant receipt
            payPlazaStreams.poll$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
          }
        }

        /*
         * This message instructs us to print the PIN receipt. In the response body is
         * the PIN receipt which must be parsed into a text body and added to the transaction
         */
        if (d.printCommandMessage) {
          msg = 'Print: ' + d.printCommandMessage.transactionStatus;
          $log.log(
            '[PayPlaza]: ' +
              msg +
              ' Trans: ' +
              d.printCommandMessage.transactionId,
            d,
          );
          updateSubject.next('Printing');

          /*
           * POS-576 A successful transaction is based on the transactionSucceeded flag
           * (Yes it is really a string boolean)
           */
          if (d.printCommandMessage.transactionSucceeded === 'true') {
            $log.log(
              '[PayPlaza] PRINT COMMAND: SUCCESS! Print POS receipt Trans: ' +
                d.printCommandMessage.transactionId,
              d,
            );
            /* If the transaction id from PayPlaza doesn't match to the transaction, this could be
             * from a previously unfinished transaction!
             */
            if (
              payPlaza.transactionId === d.printCommandMessage.transactionId
            ) {
              /*
               * A merchant receipt occurs when a signature is required for the transaction. This is usually when a credit card
               * with a mag stripe is used. This type of payment is also referred to as a card "swipe".
               */
              if (
                d.printCommandMessage.merchantReceipt === 'true' ||
                d.printCommandMessage.receiptDuplicate === 'true'
              ) {
                printHelper(d.printCommandMessage);
                sendPrintResult();
              } else {
                $log.log('[PayPlaza] Reloads transaction...');

                /*
                 * Print and Save the transaction
                 * payPlaza accepts 'printing succeed' message, even if we fail
                 * to print - because of that we provide 'reprint' button in the menu
                 */
                sendPrintResult();

                /*
                 * Printing is completed once the promise is resoled, so that's why this block does
                 * not print directly
                 */
                if (pendingTransactionDeferred) {
                  appNameSubject.next(d.printCommandMessage.appPreferredName);

                  if (d.printCommandMessage.formattedReceipt === '') {
                    sentry.captureException(
                      new Error(
                        `POS-1942 empty transaction receipt on ${JSON.stringify(
                          d,
                        )}`,
                      ),
                    );
                  }

                  // the meaningfull part is done
                  inProgressSubject.next(false);

                  pendingTransactionDeferred.resolve(
                    formatReceipt(d.printCommandMessage.formattedReceipt),
                  );

                  pendingTransactionDeferred = undefined;

                  // TODO cover by unit test
                  payPlaza.transactionId = undefined;
                }
              }
            } else {
              /*
               * A merchant receipt occurs when a signature is required for the transaction. This is usually when a credit card
               * with a mag stripe is used. This type of payment is also referred to as a card "swipe".
               */
              if (
                d.printCommandMessage.merchantReceipt === 'true' ||
                d.printCommandMessage.receiptDuplicate === 'true'
              ) {
                printHelper(d.printCommandMessage);
                sendPrintResult();
              } else {
                /*
                 * This part of the if statement is called usually when there is a breakdown in communication with the POS and PayPlaza.
                 * This is our last resort if we cannot match a transaction to what transaction is open on the payplaza server.
                 */
                $log.error(
                  '[PayPlaza] PRINT COMMAND: Transaction ID (' +
                    d.printCommandMessage.transactionId +
                    ') does NOT match expected transaction ID ' +
                    payPlaza.transactionId,
                  d,
                );
                // Only print the pin receipt
                printHelper(d.printCommandMessage);
                sendPrintResultOnError();
              }
            }
            return;
          } else {
            $log.error(
              '[PayPlaza] PRINT COMMAND: TRANSACTION! Print failure PIN receipt. Trans: ' +
                payPlaza.transactionId,
              d,
            );

            // Add the failure to the current transaction if it matches
            if (
              payPlaza.transactionId === d.printCommandMessage.transactionId
            ) {
              pendingTransactionDeferred.reject(
                formatReceipt(d.printCommandMessage.formattedReceipt),
              );
              pendingTransactionDeferred = undefined;

              // TODO cover by unit test
              payPlaza.transactionId = undefined;
            }

            // different transaction got rejected while we deal with the our
            if (pendingTransactionDeferred) {
              pendingTransactionDeferred.reject('');
              pendingTransactionDeferred = undefined;

              // TODO cover by unit test
              payPlaza.transactionId = undefined;
            }

            // Print failure receipt
            printHelper(d.printCommandMessage);
            sendPrintResultOnError();

            return;
          }
        }

        /*
         * This message is received when the transaction is fully complete
         */
        if (d.transactionCompleteMessage) {
          msg = 'Ready: ' + d.transactionCompleteMessage.resultCode;
          $log.log(
            '[PayPlaza] TRANS COMPLETE: ' +
              msg +
              ' Trans: ' +
              d.transactionCompleteMessage.transactionId,
            d,
          );

          if (d.transactionCompleteMessage.resultCode !== 'SUCCESS') {
            // is called with value that comes form pay plaza, the ones we seams to care about:
            // FAILED, INSUFFICIENT_FUNDS, AUTHORIZATION_TIMEOUT, CANCELED,
            // CARD_BLOCKED, CARD_INVALID, AMOUNT_EXCEEDED
            errorMessageSubject.next(
              $translate.instant(d.transactionCompleteMessage.resultCode),
            );
          }

          if (
            d.transactionCompleteMessage.nextAction === 'sendDisconnectMessage'
          ) {
            // TODO cover with some test
            payPlazaStreams.disconnect$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
          } else {
            payPlazaStreams.poll$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
          }
        }

        /*
         * This message returns information on the status of a pending PIN transaction
         * Based on this message we show messages to the user (cashier)
         */
        if (d.transactionUpdateMessage) {
          $log.log(
            '[PayPlaza] TRANS UPDATE: ' +
              msg +
              ' Trans: ' +
              d.transactionUpdateMessage.transactionId,
            d,
          );

          /*
           * there is no transactionId set, it must be a new one
           */
          if (angular.isUndefined(payPlaza.transactionId)) {
            payPlaza.transactionId = d.transactionUpdateMessage.transactionId;
            transactionIdSubject.next(d.transactionUpdateMessage.transactionId);
          }

          if (
            d.transactionUpdateMessage.status === 'PAYMENT_APPLICATION_SELECTED'
          ) {
            msg = 'WaitingForCustomer';
          } else {
            msg = 'Processing';
          }

          updateSubject.next(msg);

          payPlazaStreams.poll$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
        }

        /*
         * This message is received when the PIN transaction has been aborted possibly
         * by the consumer pressing Stop or removing the PIN card prematurely
         */
        if (d.abortMessage) {
          updateSubject.next('Aborted');

          $log.log(
            '[PayPlaza] ABORT: ' +
              $translate.instant('Aborted') +
              ' Trans: ' +
              payPlaza.transactionId,
          );

          // TODO cover with some test
          payPlazaStreams.disconnect$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));

          return;
        }

        /*
         * The final message received once PIN is disconnected
         */
        if (d.disconnectMessage) {
          $log.log(
            '[PayPlaza] DISCONNECT: DISCONNECTED Trans: ' +
              payPlaza.transactionId,
          );
          resetPayPlazaPIN();
        }
      }

      /**
       * @description
       * This message is sent after the connection message. This request sends
       * information about the transaction to be started including the
       * payment amount, currency, merchantref, AID (payment method). For more
       * information this process see https://en.wikipedia.org/wiki/EMV#Application_selection
       */
      function sendTransactionResponseMessage() {
        let config, amount;

        payPlazaStreams
          .createSendTransactionResponseMessageStream(
            payPlaza.merchRef,
            payPlaza.amount,
          )
          .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
          .subscribe(ppProc, ppError);
      }

      /**
       * @ngdoc
       * @name posApp.payPlaza#init
       * @methodOf posApp.payPlaza
       * @description
       * Checks if PIN is configured correctly; displays error otherwise
       */
      payPlaza.init = () => {
        payPlazaStreams.setAbortStream(payPlaza.action$);

        /// TODO move config validation to pos start up service (not created yet)
        if (
          !settingsManager.settings.pinId &&
          paymentTypes &&
          paymentTypes.rules &&
          paymentTypes.rules.pin &&
          paymentTypes.rules.pin.coupled === true
        ) {
          displayMessage.message({
            message: $translate.instant(
              'PayPlaza PIN key not configured in settings!',
            ),
            type: 'errorprompt',
            ok: true,
          });
        }
      };

      /**
       * @ngdoc
       * @name posApp.payPlaza#startTransaction
       * @methodOf posApp.payPlaza
       * @description
       * Initiates a transaction with PayPlaza web service
       * @param {object} transaction
       * transaction we will work on
       * @return {promise} promise
       * Return promise, that is resolved with the print receipt when transaction is
       * accepted.
       */
      payPlaza.startTransaction = (transaction, amount) => {
        // Guard: Prevent starting a new transaction if one is already ongoing
        if (
          typeof payPlaza.transactionId !== 'undefined' &&
          payPlaza.transactionId !== null
        ) {
          const err = new Error(
            '[PayPlaza] Attempted to start a new transaction while another is in progress.',
          );
          $log.error(err.message);
          if (sentry && sentry.captureException) {
            sentry.captureException(err);
          }
          if (displayMessage && $translate) {
            displayMessage.message({
              message: $translate.instant(
                'A PIN transaction is already in progress. Please wait until it is finished.',
              ),
              type: 'errorprompt',
              ok: true,
            });
          }
          return $q.reject('Transaction already in progress');
        }
        actionSubject.next();

        payPlaza.merchRef = transaction.merchRef;
        payPlaza.amount = amount;

        inProgressSubject.next(true);

        // Send updates of what to display to the user
        updateSubject.next('PINInsertCard');

        pendingTransactionDeferred = $q.defer();

        // Send connection message to payPlaza web service
        payPlazaStreams.connect$
          .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
          .subscribe(ppProc, ppError);

        return pendingTransactionDeferred.promise;
      };

      /**
       * @ngdoc
       * @name posApp.payPlaza#setTransactionMethod
       * @methodOf posApp.payPlaza
       * @deprecated
       * @description
       * dummy method, has no meaning in pay plaza v4
       * Method to sends chosen application to pay plaza
       */
      payPlaza.setTransactionMethod = () => {};

      /**
       * @ngdoc
       * @name posApp.payPlaza#checkServer
       * @methodOf posApp.payPlaza
       * @description
       * Checks server for any outstanding transactions that should be completed
       * @param {object} transaction
       * the transaction that we will continue if there is still some pending
       * messages
       * @returns {Promise}
       * Return promise, that resolves if there pending transaction & rejects otherwise.
       * It resolves with object that contains pickedUpTransactionPromise - a promise that
       * resolves with print receipt when pass or reject with error receipts.
       */
      // TODO rename to checkForOutstandingTransaction
      payPlaza.checkServer = (transaction) => {
        actionSubject.next();

        const defer = $q.defer();

        mask.show($translate.instant('PayPlaza synchronization'));

        payPlaza.transactionId = transaction.elecTransId;
        payPlaza.merchRef = transaction.merchRef;
        // it makes no sense for us to caculate it, but PayPlaza want's us to
        // restart transaction and provide no information about what amount it
        // was started with
        payPlaza.amount = transaction.getPendingPayment();

        function checkServerCleanup(resp) {
          // TODO cover with tests
          $log.info('[payplazav4] checkServerCleanup', resp);
          resetPayPlazaPIN();
          defer.reject(resp);
          mask.hide();
        }

        function pollPlaza() {
          $log.info('[payplazav4] pollPlaza');
          payPlazaStreams.poll$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(processPayPlazaRequests),
              wrapInSafeApply(checkServerCleanup),
            );
        }

        function disconnectFromPayPlaza(resp) {
          $log.info('[payplazav4] disconnectFromPayPlaza', resp);
          payPlazaStreams.disconnect$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(checkServerCleanup),
              wrapInSafeApply(checkServerCleanup),
            );
        }

        function sendPrintResponseMessage(resp) {
          $log.info('[payplazav4] sendPrintResponseMessage', resp);
          payPlazaStreams.sendPrintSuccess$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(processPayPlazaRequests),
              wrapInSafeApply(checkServerCleanup),
            );
        }

        function processPayPlazaRequests(resp) {
          $log.warn(
            '[payplazav4] the last transaction is not what we expected, handling it ...',
            resp,
          );
          if (resp.printCommandMessage) {
            printHelper(resp.printCommandMessage);
            sendPrintResponseMessage(resp);
          } else if (
            resp.printCompleteMessage &&
            resp.printCompleteMessage.nextAction === 'sendDisconnectMessage'
          ) {
            disconnectFromPayPlaza(resp);
          } else if (
            resp.printCompleteMessage &&
            resp.printCompleteMessage.nextAction ===
              'waitForPrintCommandMessage'
          ) {
            pollPlaza();
          }
        }

        payPlazaStreams.connect$
          .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
          .subscribe(
            wrapInSafeApply((resp) => {
              if (resp && resp.connectCompleteMessage) {
                payPlazaStreams.requestReprint$
                  .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
                  .subscribe(
                    wrapInSafeApply((resp) => {
                      if (
                        resp.printCommandMessage &&
                        payPlaza.transactionId ===
                          resp.printCommandMessage.transactionId
                      ) {
                        $log.warn(
                          '[payplazav4] Continuing unfinished transaction!',
                          resp,
                        );
                        inProgressSubject.next(true);
                        pos.selectedPayment = 'pin';

                        pendingTransactionDeferred = $q.defer();

                        defer.resolve({
                          pickedUpTransactionPromise:
                            pendingTransactionDeferred.promise,
                        });

                        // We're in an unhappy flow, but we need to get back in the happy flow
                        ppProc(resp);
                      } else {
                        // We're in an unhappy flow, and in an unknown state, need to just finish the flow
                        processPayPlazaRequests(resp);
                      }
                    }),
                    wrapInSafeApply(checkServerCleanup),
                  );
              } else {
                checkServerCleanup(resp);
              }
            }),
            wrapInSafeApply(checkServerCleanup),
          );

        return defer.promise;
      };

      /**
       * @param {object} transaction
       * transaction we will work on
       * @description
       * Resets the state of the PIN service
       */
      function resetPayPlazaPIN() {
        payPlaza.transactionId = undefined;
        payPlaza.merchRef = undefined;
        payPlaza.amount = 0;

        if (pendingTransactionDeferred) {
          pendingTransactionDeferred.reject('');
          pendingTransactionDeferred = undefined;
        }

        // Broadcast updates
        inProgressSubject.next(false);

        $log.log('[PayPlaza]: RESET');
        mask.hide();
      }

      // Handle errors that cannot be recovered from by resetting the state
      // of the PIN transaction
      function ppFatalErrorHandler(message) {
        // Reset the service and the transaction
        resetPayPlazaPIN();

        // Log the error
        ppLogErrorHandler(message);
      }

      /**
       * @ngdoc
       * @name posApp.payPlaza#abortTransaction
       * @methodOf posApp.payPlaza
       * @description
       * Stop the current transaction
       */
      payPlaza.abortTransaction = () => {
        actionSubject.next();

        updateSubject.next('abortTransaction');

        payPlazaStreams.disconnect$
          .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
          .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
      };

      /*
       * --------------------- REPRINT PIN RECEIPT ------------------------
       */
      function ppReprintError(resp) {
        let errorMsg = '[PINReprint] ECR web service';
        if (resp.cancelled) {
          // Ignore cancelled requests
          $log.warn('[PayPlaza] Cancelled request, ignoring!');
          return;
        }
        if (angular.isNumber(resp.status) && resp.status !== 200) {
          errorMsg += ' error: ' + resp.status;
        } else {
          errorMsg += ' unknown error!';
        }
        inProgressSubject.next(false);
        errorMessageSubject.next('PINServiceUnavailable');

        ppLogErrorHandler(errorMsg);

        return;
      }

      function ppReprint(d) {
        function sendPrintResult() {
          // Send print result message to payPlaza
          payPlazaStreams.sendPrintSuccess$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(ppReprint),
              wrapInSafeApply(ppReprintError),
            );
        }

        if (d.cancelled) {
          $log.warn('[PayPlaza] Cancelled request, ignoring!');
          return;
        }

        if (d.timeoutMessage) {
          $log.error('[PayPlaza] TIMEOUT');
          if (
            d.timeoutMessage.reason &&
            (d.timeoutMessage.reason === 'Reject request' ||
              d.timeoutMessage.reason === 'Request rejected')
          ) {
            /* This request was rejected by the server.
             * Do not go any further with the processing
             */
            return;
          }
          // When there is a timeout, keep polling until there is a message.
          payPlazaStreams.poll$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(ppReprint),
              wrapInSafeApply(ppReprintError),
            );
        }

        if (d.connectCompleteMessage) {
          payPlazaStreams.poll$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(
              wrapInSafeApply(ppReprint),
              wrapInSafeApply(ppReprintError),
            );
        }

        if (d.printCommandMessage) {
          $log.log(
            '[PayPlaza]: PrintCommandMessage: Trans: ' +
              d.printCommandMessage.transactionId,
          );
          // Print the pin receipt
          printHelper(d.printCommandMessage);
          sendPrintResult();
        }

        if (d.printCompleteMessage) {
          $log.log(
            '[PayPlaza] PRINT COMPLETE: Printed: ' +
              d.printCompleteMessage.resultCode,
          );
          if (d.printCompleteMessage.nextAction === 'sendDisconnectMessage') {
            // We are finished printing, send a disconnect message
            payPlazaStreams.disconnect$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(
                wrapInSafeApply(ppReprint),
                wrapInSafeApply(ppReprintError),
              );
          } else if (
            d.printCompleteMessage.nextAction === 'waitForPrintCommandMessage'
          ) {
            // We are not finished printing, there can be a second merchant receipt
            payPlazaStreams.poll$
              .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
              .subscribe(
                wrapInSafeApply(ppReprint),
                wrapInSafeApply(ppReprintError),
              );
          }
        }

        if (d.abortMessage) {
          $log.log('[PayPlaza] ABORT Trans: ' + payPlaza.transactionId);

          // TODO cover with some test
          payPlazaStreams.disconnect$
            .pipe(retry(RETRYLIMIT), take(1), takeUntil(payPlaza.action$))
            .subscribe(wrapInSafeApply(ppProc), wrapInSafeApply(ppError));
        }

        //Last message received once PIN is disconnected
        if (d.disconnectMessage) {
          $log.log('[PayPlaza] DISCONNECTED');
        }
      }

      /**
       * @ngdoc
       * @name posApp.payPlaza#reprintPINReceipt
       * @methodOf posApp.payPlaza
       * @description
       * Reprint a PIN receipt SWUI-978
       * The caveat with re-printing is a connection timeout is expected. We send
       * the request to reprint on the timeout. It's a very strange workflow.
       */
      payPlaza.reprintPINReceipt = () => {
        inProgressSubject.next(true);
        actionSubject.next();

        // Connect to pin with timeout 500 & no retry
        payPlazaStreams.connect$
          .pipe(take(1), takeUntil(payPlaza.action$))
          .subscribe(
            wrapInSafeApply(() => {
              payPlazaStreams.requestReprint$
                .pipe(take(1), takeUntil(payPlaza.action$))
                .subscribe(
                  wrapInSafeApply(ppReprint),
                  wrapInSafeApply(ppReprintError),
                );
            }),
            wrapInSafeApply(ppReprint),
          );
      };

      return payPlaza;
    },
  );
